import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Custody : famille "prise en charge temporaire".
 *
 * Prise en charge temporaire d'un bien ou d'un être vivant appartenant à un
 * tiers, avec responsabilité tracée entre la remise et la restitution.
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("garde d'animal", "dépôt
 * de vélo", "consigne") : il connaît la famille générique `custody`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app), et le cycle de vie (remise, échéance, restitution) par les dates
 * `receivedAt` / `dueBackAt` / `returnedAt`.
 *
 * Le bien pris en charge est soit une fiche `asset` (`itemAssetId`), soit un
 * simple texte libre (`description`) : l'un OU l'autre, jamais un import d'un
 * autre schéma. Le propriétaire (`ownerContactId`), le responsable
 * (`responsibleWorkerId`) et les liens work-order / booking / incident sont eux
 * aussi de simples ids optionnels ; les inspections d'état forment une liste
 * d'ids (`conditionInspectionIds`).
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset), rien de sensible signalé
 * par la fiche.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille custody (hors champs standard du socle). */
export const custodyCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "petsitting", "depot", "consigne"). */
  type: z.string().min(1),
  /** Bien pris en charge quand c'est un `asset`. Optionnel (l'un OU description). */
  itemAssetId: z.string().min(1).optional(),
  /** Description libre du bien pris en charge quand il n'a pas de fiche `asset`. Optionnel. */
  description: z.string().min(1).optional(),
  /** Propriétaire du bien confié (référence `contact`, le tiers). Optionnel. */
  ownerContactId: z.string().min(1).optional(),
  /** Date de remise (début de la responsabilité tracée). Optionnel. */
  receivedAt: z.date().optional(),
  /** Échéance de restitution prévue. Optionnel. */
  dueBackAt: z.date().optional(),
  /** Date de restitution effective (fin de la responsabilité). Optionnel. */
  returnedAt: z.date().optional(),
  /** Inspections d'état rattachées (ids de fiches `inspection`, remise et retour). Optionnel. */
  conditionInspectionIds: z.array(z.string()).optional(),
  /** Intervenant responsable de la prise en charge (référence `worker`). Optionnel. */
  responsibleWorkerId: z.string().min(1).optional(),
  /** Ordre de travail lié (référence `work-order`). Optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Réservation / créneau rattaché (référence `booking`). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Incident survenu pendant la prise en charge (référence `incident`). Optionnel. */
  incidentId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille custody (champs standard + champs métier). */
export const CustodySchema = z.object({
  ...baseEntityFields,
  ...custodyCoreFields,
});

/** Type d'une custody. */
export type Custody = z.infer<typeof CustodySchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CustodyCreateInputSchema = z.object({
  ...custodyCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une custody. */
export type CustodyCreateInput = z.infer<typeof CustodyCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CustodyUpdateInputSchema = z
  .object({ ...custodyCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une custody. */
export type CustodyUpdateInput = z.infer<typeof CustodyUpdateInputSchema>;
